import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const writeTextVideoScriptSkill: Skill = {
    name: SKILL_NAMES.writeTextVideoScript,
    description:
        'How to write the script for regular (text) scenes where there is no voice. Load before writing that copy.',
    kind: 'howto',
    surfaces: ['sketch'],
    instructions: `# Writing a Text Video Script

## Read speed sets the budget
Scene durations are timed off the text length, at roughly **2.5 words per second** in
English (slower in languages with longer words), so a script for a target duration is
a word budget:
30s: ~75 words
60s: ~155 words
120s: ~310 words

Plan against that budget before writing. Sketch the sections and their durations,
add them up, and adjust the outline if the total misses the target. Generally landing
within about 10% of the target is close enough — then adjust by adding or cutting
content.

Generally sentences should be short and to the point, with one clear concept
per scene or broken across multiple scenes.

## Structure
- **Opening.** Depends on the video type, but many will start with a title to
  establish what the video is about. If the video is promotional or more social
  where titles are less common, start with a hook or dive right into the content.
- **Close on a real outro** — a takeaway, wrap-up, or call to action. Never stop
  abruptly after the last point.
- For scripts communicating information (explainers, walkthroughs, etc) generally
  it's best to divide the content into "sections" with headings or landmark scenes
  to anchor the viewer. Each of these sections should generally be 10-30 seconds long.

## Write it to be read, not heard
This is the difference that matters most versus a voiceover script. There's no
narrator to carry meaning through tone, pacing, or emphasis; the words are alone on
screen.
- **Every scene's text must stand entirely on its own.** The viewer gets the point
  with zero outside context.
- **Trim for brevity, never for meaning.** "The shift" is not a scene — it's a
  fragment that means nothing without a narrator.
- Prefer short, simple sentences. Break complex sentences apart rather than nesting
  clauses: a sentence a reader has to re-parse is a sentence they miss before the
  scene changes. A run of quick scenes, each making one point, reads well — just
  remember each one still holds for 3.4s, so keep the word budget in mind.
- Each sentence should read as one complete thought that can hold a scene on its own.
  A headline plus disconnected fragments reads as a presentation slide, not a video.
- Abbreviations are fine here (unlike in narration) because the viewer can read them!

## Content fidelity
How faithful to be depends on whether the user gave you source material:
- **Summarizing a source** (an article, a document, a link): pull content from it and
  adapt freely — you are not bound to its structure — but do **not** invent
  information, quotes, or data points that aren't in it. Cut whatever isn't needed
  for the story the user asked for.
- **Writing from a topic or brief** with no source: be creative and elaborate. When
  you're short on specifics, make a reasonable, informed guess rather than padding
  with generalities. **Be specific, not general** — a vague script is a worse failure
  than an imperfect detail.

In both cases:
- Weave any additional context the user supplied in naturally, not bolted on.
- Match the **tone** of the reference material unless the user asked for something
  else, and match its perspective (first vs. third person) the same way.
- Write in the language of the reference material unless told otherwise.

## Formatting rules
- **Never put an em dash (—) in the script.** Write as separate sentences, or join with a
  comma or a conjunction.
- Only ever quote a named speaker; never attribute a quote to the source document itself.
- Plain text only: no markdown, no asterisks for emphasis, no speaker labels, no
  stage directions.
- No emojis or emoticons.`,
};
